/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, useContext, useEffect, useState } from 'react';

import { Button, Grid } from '@coursera/cds-core';

import type {
  AiReviewPartSuggestion,
  AiSuggestionGradedTextBoxDetails,
  AiSuggestionOptionsDetails,
  AiSuggestionUngradedTextBoxDetails,
  AssignmentRubric,
  ContentCmlContent,
  ManuallyGradedQuestionElement,
  QuestionRubric,
  QuestionRubricPrompt,
  QuestionRubricPromptSchemaDetailsMultiLineInput,
  QuestionRubricPromptSchemaDetailsOptions,
} from 'bundles/grading/api/types';
import { useAiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import RateSuggestion from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/RateSuggestion';
import MultiLineInputSuggestion from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/suggestion-review-parts/MultiLineInputSuggestion';
import OptionsSuggestion from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/suggestion-review-parts/OptionsSuggestion';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';

import _t from 'i18n!nls/grading';

type Props = {
  suggestion: AiReviewPartSuggestion;
  applySuggestion: () => void;
  currentRubric: AssignmentRubric | QuestionRubric;
};

const styles = {
  container: css`
    background-color: var(--cds-color-interactive-stroke-primary-focus-invert);
    color: var(--cds-color-neutral-primary);
    border-radius: var(--cds-spacing-50);
    padding: var(--cds-spacing-150);
    margin-top: var(--cds-spacing-150);
  `,
  applied: css`
    background-color: var(--cds-color-neutral-disabled-weak);
    color: var(--cds-color-neutral-primary-weak);
  `,
  button: css`
    max-width: 100%;
  `,
};

const ReviewPartSuggestion = ({ suggestion, applySuggestion, currentRubric }: Props) => {
  const { isSaving, isSubmitting, hasUnsavedChanges } = useContext(AutoSaveContext);
  const { currentAiReviewPartSuggestion, currentAiReviewPartSuggestionApplied, trackSuggestionApplied } =
    useAiSuggestionsContext();
  const [isApplied, setIsApplied] = useState(currentAiReviewPartSuggestionApplied);

  // once a suggestion has been applied, it must remain applied for this user
  // ensure the state is updated when needed
  useEffect(() => {
    setIsApplied(currentAiReviewPartSuggestionApplied);
  }, [currentAiReviewPartSuggestion, currentAiReviewPartSuggestionApplied]);

  if (!currentAiReviewPartSuggestion) {
    return null;
  }

  const handleApplyOnClick = () => {
    applySuggestion();
    setIsApplied(true);
    trackSuggestionApplied();
  };

  let reviewPartSuggestionBody = null;
  if (suggestion?.typeName) {
    switch (suggestion.typeName) {
      case 'singleLineInput':
      case 'multiLineInput':
        reviewPartSuggestionBody = (
          <MultiLineInputSuggestion
            suggestedInput={
              (suggestion.details as AiSuggestionGradedTextBoxDetails | AiSuggestionUngradedTextBoxDetails).input
            }
            suggestedScore={(suggestion.details as AiSuggestionGradedTextBoxDetails)?.score}
            reviewPoints={
              (
                (
                  (currentRubric?.formElementNode?.element as ManuallyGradedQuestionElement)
                    ?.prompt as QuestionRubricPrompt
                )?.reviewPartSchemaDetails as QuestionRubricPromptSchemaDetailsMultiLineInput
              )?.points
            }
          />
        );
        break;
      case 'yesNo':
      case 'options': {
        const currentOption = (
          ((currentRubric?.formElementNode?.element as ManuallyGradedQuestionElement)?.prompt as QuestionRubricPrompt)
            ?.reviewPartSchemaDetails as QuestionRubricPromptSchemaDetailsOptions
        )?.options?.find(
          (option) => option.optionId === (suggestion.details as AiSuggestionOptionsDetails).optionChoice
        );
        reviewPartSuggestionBody =
          typeof currentOption?.points === 'number' ? (
            <OptionsSuggestion
              suggestedScore={currentOption.points}
              suggestedInputCml={{
                typeName: 'cml',
                definition: {
                  dtdId: (currentOption.display as ContentCmlContent).dtdId,
                  value: (currentOption.display as ContentCmlContent).cmlValue,
                },
              }}
              feedback={(suggestion.details as AiSuggestionOptionsDetails).feedback}
            />
          ) : null;
        break;
      }
      case 'boxViewDocumentAnnotation':
      default:
        break;
    }
  }
  if (!reviewPartSuggestionBody) {
    return null;
  }
  return (
    <Fragment>
      <Grid container css={[styles.container, isApplied && styles.applied]}>
        <Grid item container xs={9}>
          {reviewPartSuggestionBody}
        </Grid>
        <Grid item container xs={3} alignItems="center" justifyContent="flex-end" spacing={12}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleApplyOnClick}
            disabled={isSaving || isSubmitting || hasUnsavedChanges || isApplied}
            css={styles.button}
          >
            {isApplied ? _t('Applied') : _t('Apply')}
          </Button>
        </Grid>
      </Grid>
      <RateSuggestion />
    </Fragment>
  );
};

export default ReviewPartSuggestion;
